import React, { useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../hooks/redux';
import { PATHS } from '../constants/Paths.constants';
import { selectIsSuperAdmin } from '../store/selectors/authSelectors';
import { isOidcAuth } from '../config/featureFlags';
import { userManager } from './OidcAuthProvider';
import { isLogoutInProgress } from '../utils/Auth.util';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Module-level flag so that only one signinRedirect is ever started,
// even if multiple ProtectedRoute instances render before the redirect completes.
let signinRedirectStarted = false;

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const oidc = isOidcAuth();

  if (!isAuthenticated) {
    if (oidc) {
      if (isLogoutInProgress()) {
        return <Navigate to={PATHS.LOGOUT} replace />;
      }

      // Do not start a new redirect if the browser is currently processing
      // an OIDC callback (URL contains the authorization code).
      const params = new URLSearchParams(window.location.search);
      const isCallback = params.has('code') && params.has('state');

      if (!isCallback && !signinRedirectStarted) {
        signinRedirectStarted = true;
        void userManager
          .signinRedirect({
            state: { returnTo: location.pathname + location.search },
          })
          .catch((err) => {
            // Allow retry on next render if the redirect itself failed
            signinRedirectStarted = false;
            console.error('[OIDC] signinRedirect error:', err);
          });
      }
      return null;
    }
    return <Navigate to={PATHS.LOGIN} replace />;
  }

  // Reset the flag once the user is authenticated so future logouts work
  signinRedirectStarted = false;

  const normalizedPathname =
    location.pathname.replace(/\/+$/, '') || PATHS.ROOT;
  const superAdminAllowedPaths: string[] = [
    PATHS.USER_MANAGEMENT,
    PATHS.SETTINGS,
    PATHS.SETTINGS_ACCOUNT,
  ];
  const canSuperAdminAccessPath =
    superAdminAllowedPaths.includes(normalizedPathname);

  if (isSuperAdmin && !canSuperAdminAccessPath) {
    return <Navigate to={PATHS.USER_MANAGEMENT} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
